import { useRef, useState, useEffect } from 'react'
import {
  faCircleUser,
  faChevronDown,
  faSeedling,
} from '@fortawesome/pro-regular-svg-icons'
import type { IconProp } from '@fortawesome/fontawesome-svg-core'

import { SidebarConfigProps } from '@/reducers/sidebar.slice'
import { User } from '@/reducers/auth/userReducer'
import { mtc, TextColors, TypographyStyles } from '@/styles'
import { Icon, List, Popover, Text, Tooltip } from '@/ui'
import {
  NavListRestructured,
  NavAnnouncementProps,
} from './NavListRestructured'
import { Header } from './Header'
import {
  rootStyles,
  footerStyles,
  footerMenuStyles,
  listStyles,
  listItemStyles,
  iconStyles,
} from './stylesRestructured'

interface ExpandedRestructuredProps {
  device: string
  footerOptions: Array<{ icon: IconProp; onClick: () => void; text: string }>
  categories?: Record<string, SidebarConfigProps> | null
  isOpen: boolean
  setIsOpen: (isOpen: boolean) => void
  user?: User | null
  selectedCategory?: string | null
  isSmallDevice: boolean
  footerBanner?: React.ReactNode
  onAskHaven?: () => void
  navAnnouncement?: NavAnnouncementProps
}

export const ExpandedRestructured = ({
  device,
  footerOptions,
  categories,
  isOpen,
  setIsOpen,
  user,
  selectedCategory,
  isSmallDevice,
  footerBanner,
  onAskHaven,
  navAnnouncement,
}: ExpandedRestructuredProps) => {
  const scrollRef = useRef<HTMLDivElement>(null)
  const [showShadow, setShowShadow] = useState(false)
  const [open, setOpen] = useState(false)

  useEffect(() => {
    const el = scrollRef.current
    if (!el) return () => {}

    // Show the footer shadow whenever the nav content overflows behind it.
    // A ResizeObserver keeps this correct as content mounts/changes (the old
    // one-shot check ran before the nav rendered, so it never triggered).
    const update = () => setShowShadow(el.scrollHeight > el.clientHeight + 1)
    update()

    // Observe the growing content, not just the fixed-size scroll container,
    // so overflow is detected as nav items mount.
    const observer = new ResizeObserver(update)
    observer.observe(el)
    if (el.firstElementChild) observer.observe(el.firstElementChild)
    return () => observer.disconnect()
  }, [categories])

  return (
    <nav className={mtc(rootStyles(), isSmallDevice ? 'w-full' : 'w-64')}>
      {/* scrollable content */}
      <div
        ref={scrollRef}
        className="flex flex-col justify-between flex-1 overflow-y-auto overflow-x-hidden space-y-6 pb-2"
      >
        <div className="flex flex-col gap-10">
          <Header
            device={device}
            handleIconClick={() => setIsOpen(false)}
            isOpen={isOpen}
          />

          <NavListRestructured
            categories={categories}
            selectedCategory={selectedCategory}
            navAnnouncement={navAnnouncement}
          />
        </div>

        {footerBanner && <div className="px-4 pb-2">{footerBanner}</div>}
      </div>
      {/* Sticky footer */}
      <footer className={mtc(footerStyles({ showShadow }))}>
        {onAskHaven && (
          <button
            type="button"
            className={mtc(footerMenuStyles())}
            onClick={onAskHaven}
          >
            <div className="flex flex-1 items-center gap-3">
              <Icon
                icon={faSeedling}
                size="1x"
                color="text-neutral-700"
                className="w-5 h-5 shrink-0"
                backgroundClassName="w-5 h-5 shrink-0"
              />
              <Text
                variant={TypographyStyles.LABEL_SM}
                color={TextColors.EMPHASIS}
              >
                Ask Haven
              </Text>
            </div>
          </button>
        )}

        <Popover open={open} onOpenChange={setOpen}>
          <Popover.Trigger asChild>
            <button type="button" className={mtc(footerMenuStyles())}>
              <div className="flex flex-1 items-center gap-3 min-w-0 overflow-hidden">
                <Icon
                  icon={faCircleUser}
                  size="1x"
                  color="text-neutral-700"
                  className="w-5 h-5 shrink-0"
                  backgroundClassName="w-5 h-5 shrink-0"
                />
                <Tooltip>
                  <Tooltip.Trigger asChild>
                    <Text
                      variant={TypographyStyles.LABEL_SM}
                      color={TextColors.EMPHASIS}
                      className="truncate"
                    >
                      {user?.firstName} {user?.lastName}
                    </Text>
                  </Tooltip.Trigger>
                  <Tooltip.Content side="top" align="start">
                    <Text variant={TypographyStyles.BODY_SM}>
                      {user?.firstName} {user?.lastName}
                    </Text>
                  </Tooltip.Content>
                </Tooltip>
              </div>
              <Icon
                icon={faChevronDown}
                className={mtc(
                  'h-4 w-4 shrink-0 text-neutral-500 transition-transform duration-200',
                  open && 'rotate-[-180deg]'
                )}
              />
            </button>
          </Popover.Trigger>

          <Popover.Content
            align="end"
            className="w-inherit rounded-2xl py-1.5 px-2"
          >
            <List className={mtc(listStyles())}>
              {footerOptions.map(({ icon, onClick, text }) => (
                <List.Item
                  onClick={onClick}
                  key={text}
                  className={mtc(listItemStyles())}
                >
                  <Icon icon={icon} className={mtc(iconStyles())} />

                  <Text
                    variant={TypographyStyles.BODY_SM}
                    className="flex-1 text-neutral-600"
                  >
                    {text}
                  </Text>
                </List.Item>
              ))}
            </List>
          </Popover.Content>
        </Popover>
      </footer>
    </nav>
  )
}
